import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Card, CardContent, Chip, Grid, Stack, Typography, Divider, Button } from '@mui/material';
import dayjs from 'dayjs';
import JobsContainersGroup from './JobsContainersGroup';
import ConfirmJobDialog from './ConfirmJobDialog';

const JobCard = ({ job, onConfirm }) => {
  const lrd = job?.booking?.latestReturnDate;
  const erd = job?.booking?.earliestReceivingDate;
  const diff = lrd ? dayjs(lrd).diff(dayjs(), 'day') : null;
  const vehiclesCount = (job?.containers || []).reduce((a, c) => a + (c?.shipmentItems?.length || 0), 0);
  const booking = job?.booking;
  const containers = job?.containers || [];
  const firstContainers = containers.slice(0, 3).map((c) => `#${c?.containerNumber || 'N/A'} (Seal ${c?.sealNumber || '-'})`);
  const moreContainers = Math.max(containers.length - firstContainers.length, 0);
  const containerText = `${firstContainers.join(', ')}${moreContainers > 0 ? `, +${moreContainers} more` : ''}`;

  return (
    <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
      <CardContent>
        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2">Job #{job.id}</Typography>
            <Chip size="small" label={job.jobStatus} color={job.jobStatus === 'Finished' ? 'success' : job.jobStatus === 'Canceled' ? 'error' : 'warning'} />
          </Stack>
          <Typography variant="body2" color="text.secondary">{job?.client?.name || '-'}</Typography>
          <Divider sx={{ my: 0.5 }} />
          <Stack spacing={0.5}>
            <Typography variant="body2">
              <strong>Dates:</strong> Start {job.startDate || '-'} • ERD {erd || '-'} • LRD {lrd || '-'}{' '}
              {typeof diff === 'number' && (
                <Chip size="small" sx={{ ml: 0.5 }} color={diff < 0 ? 'error' : diff <= 2 ? 'warning' : 'default'} label={`${diff}d`} />
              )}
            </Typography>
            <Typography variant="body2">
              <strong>Booking:</strong> {booking?.number || '-'} • Vessel {booking?.vesselName || '-'} • POL {booking?.portLoading?.name || '-'} • Dest {booking?.destination?.name || '-'} • Line {booking?.shippingLine?.name || '-'}
            </Typography>
            <Typography variant="body2">
              <strong>Containers ({containers.length}):</strong> {containers.length ? containerText : '-'}
            </Typography>
            <Typography variant="body2">
              <strong>Vehicles:</strong> {vehiclesCount}
            </Typography>
          </Stack>
        </Stack>
        <Box sx={{ mt: 1 }}>
          <JobsContainersGroup job={job} singleRow vehicleLimit={10} />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
          <Button
            size="small"
            variant="contained"
            color="success"
            disabled={job.jobStatus === 'Finished' || job.jobStatus === 'Canceled'}
            onClick={() => onConfirm && onConfirm(job)}
          >
            Confirm Completed
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

const JobsCards = ({ onConfirmDialogOpenChange }) => {
  const [confirmState, setConfirmState] = useState({ open: false, job: null });
  const stateV2 = useSelector((s) => s.manageV2.getManage);
  const legacy = useSelector((s) => s.manage);
  const raw = (stateV2?.object && Object.keys(stateV2.object || {}).length > 0) ? stateV2.object : (legacy?.object || {});
  const page = raw?.body && (Array.isArray(raw.body.content) || typeof raw.body.totalElements === 'number') ? raw.body : raw;
  const items = page.content || [];

  return (
    <Box>
      <ConfirmJobDialog
        open={confirmState.open}
        job={confirmState.job}
        onClose={() => { setConfirmState({ open: false, job: null }); onConfirmDialogOpenChange && onConfirmDialogOpenChange(false); }}
      />
      <Grid container spacing={2}>
        {items.map((job) => (
          <Grid key={job.id} item xs={12} sm={6} md={4} lg={3}>
            <JobCard job={job} onConfirm={(j) => { setConfirmState({ open: true, job: j }); onConfirmDialogOpenChange && onConfirmDialogOpenChange(true); }} />
          </Grid>
        ))}
        {items.length === 0 && (
          <Grid item xs={12}>
            <Typography align="center" color="text.secondary">No jobs</Typography>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default JobsCards;
